import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { formatDate as formatAngularDate } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApprovalService } from '../../features/approvals/services/approval.service';
import {
  ApprovalRequestDto,
  ApprovalActionRequest,
  CompanyPayload,
} from '../../features/approvals/models/approval.model';
import { NotificationActionService } from '../../core/services/notification-action.service';
import { ToastService } from '../../core/services/toast.service';
import { FileDownloadService } from '../../core/services/file-download.service';

interface DiffRow {
  label: string;
  before: string;
  after: string;
  changed: boolean;
  /** Optional secondary line rendered under the value (e.g. loan code under the
   *  loan description). Used by the two-line "Loan" column on Loan screens. */
  subBefore?: string;
  subAfter?: string;
}

/**
 * Global, read-only Approval Request Details dialog.
 *
 * Hosted once in the layout so a notification click can open it on ANY page without a route
 * change. Shows the request's meta, submitted/changed field data and approval history — the
 * read-only view. Approve / reject / return remain on the dedicated Company Approvals screen,
 * where the approver acts deliberately.
 */
@Component({
  standalone: false,
  selector: 'app-request-detail-dialog',
  templateUrl: './request-detail-dialog.component.html',
  styleUrls: ['./request-detail-dialog.component.scss'],
})
export class RequestDetailDialogComponent implements OnInit, OnDestroy {
  visible = false;
  loading = false;
  selectedReq: ApprovalRequestDto | null = null;

  /** Dialog header; overridable per opener (default for approver screens). */
  dialogTitle = 'Approval Request Details';
  private static readonly DEFAULT_TITLE = 'Approval Request Details';

  // ── Action (approve / reject / return) dialog ───────────────────────────────
  actionType: 'approve' | 'reject' | 'return' | null = null;
  actionComment = '';
  actioning = false;

  get actionDialogVisible(): boolean { return this.actionType !== null; }
  set actionDialogVisible(v: boolean) { if (!v) this.actionType = null; }

  private parsedBefore: CompanyPayload | null = null;
  private parsedAfter: CompanyPayload | null = null;

  /** Parsed bulk-import envelope (jsonAfter) — only populated for BULK_IMPORT requests. */
  bulkImportInfo: Record<string, any> | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly approvalService: ApprovalService,
    private readonly notificationAction: NotificationActionService,
    private readonly toast: ToastService,
    private readonly router: Router,
    private readonly fileDownload: FileDownloadService,
  ) {}

  ngOnInit(): void {
    this.notificationAction.openRequest$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ requestId, title }) => {
        if (requestId > 0) this.open(requestId, title);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private open(requestId: number, title?: string): void {
    this.dialogTitle = title?.trim() || RequestDetailDialogComponent.DEFAULT_TITLE;
    this.visible = true;
    this.loading = true;
    this.selectedReq = null;
    this.parsedBefore = null;
    this.parsedAfter = null;
    this.bulkImportInfo = null;

    this.approvalService.getRequest(requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.loading = false;
          if (res.success && res.data) {
            this.selectedReq = res.data;
            this.parsedBefore = this.tryParse(res.data.jsonBefore);
            this.parsedAfter = this.tryParse(res.data.jsonAfter);
            // Bulk imports render a dedicated layout (same as the Employee
            // Approvals "Bulk Import Approval Details" popup) rather than a diff.
            if (res.data.transactionType === 'BULK_IMPORT') {
              try { this.bulkImportInfo = JSON.parse(res.data.jsonAfter || '{}'); }
              catch { this.bulkImportInfo = {}; }
            }
          } else {
            this.toast.error(res.message || 'Failed to load request details.');
            this.visible = false;
          }
        },
        error: () => {
          this.loading = false;
          this.visible = false;
          this.toast.error('Failed to load request details.');
        },
      });
  }

  close(): void {
    this.visible = false;
    this.selectedReq = null;
    this.parsedBefore = null;
    this.parsedAfter = null;
    this.bulkImportInfo = null;
    this.dialogTitle = RequestDetailDialogComponent.DEFAULT_TITLE;
  }

  // ── Bulk import (mirrors the Employee Approvals "Bulk Import Approval Details") ──

  /** True when the open request is an employee bulk import. */
  get isBulkImport(): boolean {
    return this.selectedReq?.transactionType === 'BULK_IMPORT';
  }

  private static readonly MI_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  /**
   * True when the open request belongs to the Monthly Input screen. Monthly Input
   * stores a validated save/delete ENVELOPE (period + category + fields + rows, or
   * period + codes) rather than a per-field entity, so it renders a dedicated
   * read-only summary instead of the generic diff/record table.
   */
  get isMonthlyInput(): boolean {
    return this.selectedReq?.screenCode === 'MONTHLY_INPUT';
  }

  /** Read-only summary lines for a Monthly Input save/delete request. */
  get monthlyInputSummary(): { label: string; value: string }[] {
    if (!this.selectedReq) return [];
    const env = this.unwrapEnvelope(this.parsedAfter);
    const month = Number(this.field(env, 'month'));
    const year = Number(this.field(env, 'year'));
    const period = month >= 1 && month <= 12 && year > 0
      ? `${RequestDetailDialogComponent.MI_MONTHS[month - 1]} ${year}`
      : '—';

    const rows: { label: string; value: string }[] = [{ label: 'Period', value: period }];

    if (this.selectedReq.transactionType === 'DELETE') {
      const codes = this.miArray(env['codes'] ?? env['Codes']);
      rows.push({ label: 'Employees to Delete', value: String(codes.length) });
      if (codes.length) {
        rows.push({ label: 'Employee Codes', value: codes.slice(0, 50).join(', ') + (codes.length > 50 ? ' …' : '') });
      }
    } else {
      const cat = this.field(env, 'cCode') || '—';
      const fields = this.miArray(env['fields'] ?? env['Fields']);
      const rowsArr = this.miArray(env['rows'] ?? env['Rows']);
      rows.push({ label: 'Category', value: cat });
      rows.push({ label: 'Employee Rows', value: String(rowsArr.length) });
      rows.push({ label: 'Fields', value: fields.length ? fields.join(', ') : '—' });
    }
    return rows;
  }

  /** Flatten an envelope array to string tokens (row objects → their code). */
  private miArray(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    return v
      .map(x => {
        if (x == null) return '';
        if (typeof x === 'object') {
          const o = x as Record<string, unknown>;
          return String(o['code'] ?? o['Code'] ?? '');
        }
        return String(x);
      })
      .filter(s => s !== '');
  }

  /**
   * Item count from the bulk envelope (tolerant of casing / shape). Reads the
   * Employee Import keys (totalEmployees / employees) AND the Details Import keys
   * (totalRows / rows) so one popup serves both screens.
   */
  getBulkEmployeeCount(): number {
    if (!this.bulkImportInfo) return 0;
    return this.bulkImportInfo['totalEmployees']
      || this.bulkImportInfo['TotalEmployees']
      || this.bulkImportInfo['employees']?.length
      || this.bulkImportInfo['Employees']?.length
      || this.bulkImportInfo['totalRows']
      || this.bulkImportInfo['TotalRows']
      || this.bulkImportInfo['rows']?.length
      || this.bulkImportInfo['Rows']?.length
      || 0;
  }

  /** Label for the bulk count — "Total Employees" for EMPLOYEE, "Total Rows" otherwise. */
  get bulkCountLabel(): string {
    return this.selectedReq?.screenCode === 'EMPLOYEE' ? 'Total Employees' : 'Total Rows';
  }

  /**
   * Screens whose bulk-import stores the original uploaded file and exposes a
   * download endpoint. EMPLOYEE -> /download/approval/{id}/excel;
   * DETAILS_IMPORT -> /employees/details-import/approval/{id}/excel.
   */
  private readonly downloadEndpointByScreen: Record<string, (id: number) => string> = {
    EMPLOYEE: id => `download/approval/${id}/excel`,
    DETAILS_IMPORT: id => `employees/details-import/approval/${id}/excel`,
    // Monthly Input imports reuse the screen's UPDATE/DELETE save (not BULK_IMPORT);
    // the uploaded file is keyed by the import run id carried in the save envelope.
    MONTHLY_INPUT: id => `payroll/monthly-import/approval/${id}/excel`,
  };

  /**
   * True when the open request's screen stores the original uploaded file and can
   * serve it for download (Employee Master Import, Details Import, or a Monthly
   * Input request that originated from an Excel import).
   */
  get canDownloadFile(): boolean {
    const screen = this.selectedReq?.screenCode ?? '';
    if (!this.downloadEndpointByScreen[screen]) return false;
    // Employee / Details import: driven by the BULK_IMPORT envelope.
    if (this.isBulkImport) return !!this.getBulkFileName();
    // Monthly Input: driven by the save envelope's import run id (present only when
    // the save came from an import). The file name is best-effort for display.
    if (this.isMonthlyInput) return !!this.monthlyImportRunId;
    return false;
  }

  /** Import run id from a Monthly Input save envelope — set only for import-originated saves. */
  private get monthlyImportRunId(): string {
    const env = this.unwrapEnvelope(this.parsedAfter);
    const runId = this.field(env, 'runId');
    return runId && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(runId) ? runId : '';
  }

  /**
   * Original uploaded file name for display / download. Reads the BULK_IMPORT
   * envelope (Employee / Details) or, for Monthly Input, the save envelope's
   * fileName. Tolerant of casing.
   */
  getBulkFileName(): string | null {
    if (this.isMonthlyInput) {
      const env = this.unwrapEnvelope(this.parsedAfter);
      return this.field(env, 'fileName') || null;
    }
    if (!this.bulkImportInfo) return null;
    return this.bulkImportInfo['fileName']
      || this.bulkImportInfo['filename']
      || this.bulkImportInfo['FileName']
      || null;
  }

  /** Category code from the bulk envelope. Employee import uses "cCode"; the
   *  increment import envelope uses "categoryCode" — accept either. */
  getBulkCategory(): string | null {
    if (!this.bulkImportInfo) return null;
    return this.bulkImportInfo['cCode']
      || this.bulkImportInfo['CCode']
      || this.bulkImportInfo['categoryCode']
      || this.bulkImportInfo['CategoryCode']
      || null;
  }

  /**
   * Download the maker's original uploaded .xlsx via the shared, authenticated
   * download service (same endpoint the Employee Approvals page uses).
   */
  downloadExcelFile(): void {
    const requestId = this.selectedReq?.requestId;
    const screen = this.selectedReq?.screenCode ?? '';
    const buildPath = this.downloadEndpointByScreen[screen];
    if (!requestId || !buildPath) {
      this.toast.error('No Excel file available for download.');
      return;
    }
    const fileName = this.getBulkFileName() || `BulkImport_${requestId}.xlsx`;
    this.fileDownload.download(buildPath(requestId), fileName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.toast.success('Excel file download started.'),
        error: () => this.toast.error('Failed to download the Excel file.'),
      });
  }

  /** Whether the current user may act on this request's current level (server-resolved). */
  get canAct(): boolean {
    return this.selectedReq?.canAct === true;
  }

  /**
   * Employee requests expose an Edit action that opens the full Employee form (loaded from this
   * request) so the complete employee information can be reviewed/edited. Offered only for
   * EMPLOYEE requests that are still pending (not returned, rejected, approved or cancelled).
   */
  get canEdit(): boolean {
    const s = this.selectedReq?.status;
    return this.selectedReq?.screenCode === 'EMPLOYEE'
      && this.selectedReq?.transactionType !== 'BULK_IMPORT'
      && (s === 'PENDING' || s === 'SUBMITTED')
      // Only while still at the first level — once it advances to a higher level,
      // the maker can no longer edit it.
      && this.selectedReq?.currentLevel === 1;
  }

  /**
   * Open the Employee form for this request. CREATE requests have no employee record yet
   * (transactionId is the "NEW" placeholder) so go to the create form; otherwise open the edit
   * form keyed by the employee code (transactionId). The requestId is passed so the form loads
   * the submitted payload. Closes the dialog first.
   */
  editRequest(): void {
    const req = this.selectedReq;
    if (!req) return;
    this.close();

    const requestId = req.requestId;
    if (req.transactionType === 'CREATE') {
      this.router.navigate(['/employees/create'], { queryParams: { requestId } });
    } else {
      this.router.navigate(['/employees/edit', req.transactionId], { queryParams: { requestId } });
    }
  }

  // ── Approve / Reject / Return (same flow as the Company Approvals screen) ────

  actionDialogTitle(): string {
    return this.actionType === 'approve' ? 'Confirm Approval'
         : this.actionType === 'reject'  ? 'Reject Request'
         : 'Return to Maker';
  }

  openAction(type: 'approve' | 'reject' | 'return'): void {
    this.actionType = type;
    this.actionComment = '';
  }

  closeAction(): void {
    this.actionType = null;
    this.actionComment = '';
  }

  confirmAction(): void {
    if (!this.selectedReq || !this.actionType) return;

    if (this.actionType === 'reject' && !this.actionComment.trim()) {
      this.toast.warning('Rejection reason is required.');
      return;
    }

    const id = this.selectedReq.requestId;
    const payload: ApprovalActionRequest = { comments: this.actionComment.trim() || null };
    const type = this.actionType;
    this.actioning = true;

    const obs = type === 'approve'
      ? this.approvalService.approve(id, payload)
      : type === 'reject'
        ? this.approvalService.reject(id, payload)
        : this.approvalService.returnToMaker(id, payload);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        this.actioning = false;
        this.closeAction();
        if (res.success) {
          const label = type === 'approve' ? 'approved'
                      : type === 'reject'  ? 'rejected'
                      : 'returned to maker';
          this.toast.success(`Request #${id} ${label} successfully.`);
          this.close();
          // Let any open approvals/requests list know it should refresh.
          this.notificationAction.requestsChanged();
        } else {
          this.toast.error(res.message || 'Action failed.');
        }
      },
      error: () => {
        this.actioning = false;
        this.toast.error('An error occurred. Please try again.');
      },
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  /** Entity word for the current request's screen (Company / Category / Employee,
   *  or a humanized noun derived from the screen code for other screens — e.g.
   *  LOAN_ENTRY -> "Loan Entry", LOAN_FORECLOSE_REVERSE -> "Loan Foreclose Reverse"). */
  get entity(): string {
    switch (this.selectedReq?.screenCode) {
      case 'COMPANY':  return 'Company';
      case 'CATEGORY': return 'Category';
      case 'EMPLOYEE': return 'Employee';
      case 'MONTHLY_INPUT': return 'Monthly Input';
      default:         return this.screenNoun();
    }
  }

  /** Humanized entity noun from the screen code (e.g. "LOAN_ENTRY" -> "Loan Entry"). */
  private screenNoun(): string {
    const sc = this.selectedReq?.screenCode?.trim();
    if (!sc) return 'Record';
    return sc
      .replace(/[_\-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  // ── Loan screens: identity from the transaction id ──────────────────────────
  // Loan requests set transactionId = "<EMPLOYEE_CODE>|<LOAN_CODE>|<yyyy-MM-dd>"
  // (LoanService.TxnId). The employee NAME and the loan DESCRIPTION are not in
  // the saved payload, so for a frontend-only view we surface the codes we have:
  // the employee code in the meta header, and the loan code in the details table.

  /** True when the open request is one of the four Loan screens. */
  get isLoanScreen(): boolean {
    const sc = this.selectedReq?.screenCode;
    return sc === 'LOAN_ENTRY' || sc === 'LOAN_DEVIATION'
        || sc === 'LOAN_FORECLOSE' || sc === 'LOAN_FORECLOSE_REVERSE';
  }

  /** True when the open request is a Loan Deviation (curated single-record view). */
  get isLoanDeviation(): boolean {
    return this.selectedReq?.screenCode === 'LOAN_DEVIATION';
  }

  /** True when the open request is a Separation or Release (curated single-record view). */
  get isSeparationScreen(): boolean {
    const sc = this.selectedReq?.screenCode;
    return sc === 'EMPLOYEE_SEPARATION' || sc === 'EMPLOYEE_RELEASE';
  }

  /** True when the open request is a Payroll Processing run (curated single-record view). */
  get isPayrollScreen(): boolean {
    return this.selectedReq?.screenCode === 'PAYROLL_PROCESS';
  }

  /** True when the open request is a Supplementary or LOP-Credit entry (curated
   *  single-record view). Both codes share one curated builder. */
  get isSupplementaryScreen(): boolean {
    const sc = this.selectedReq?.screenCode;
    return sc === 'SUPPLEMENTARY' || sc === 'LOP_CREDIT';
  }

  /** True when the open request is a manual Employee Increment entry/delete
   *  (curated single-record view). Excludes the bulk import, which renders via the
   *  dedicated Employee-Data / download layout. */
  get isIncrementScreen(): boolean {
    return this.selectedReq?.screenCode === 'INCREMENT_ENTRY'
      && this.selectedReq?.transactionType !== 'BULK_IMPORT';
  }

  /**
   * True for the curated single-record screens (Loan Deviation, Separation /
   * Release, Payroll Processing, Supplementary / LOP Credit, Increment). Their row
   * builders always populate `after` (never `before`), so the record table must
   * read `after` even when the transaction type is DELETE — otherwise a DELETE
   * request (txn type DELETE) would show the empty `before` side as all dashes.
   */
  get isCuratedRecord(): boolean {
    return this.isLoanDeviation || this.isSeparationScreen || this.isPayrollScreen
      || this.isSupplementaryScreen || this.isIncrementScreen;
  }

  /**
   * Company display for a payroll-run request's meta header: resolved company
   * name (enricher: displayLabels.companyName) with the code — "C0001 — Acme";
   * falls back to the compCode when the name is absent.
   */
  get payrollCompanyDisplay(): string {
    const code = (this.selectedReq?.compCode ?? '').trim() || '—';
    const name = this.selectedReq?.displayLabels?.['companyName']?.trim();
    return name ? `${code} — ${name}` : code;
  }

  /**
   * Curated single-record rows for a Payroll Processing run: Operation
   * (Process/Delete), Period (Month Year), Scope, the employee (Single) or
   * category filter (Selective), settlement flag and execution type — read from
   * the stored envelope's request payload.
   */
  private payrollRows(): DiffRow[] {
    const payload = this.unwrapEnvelope(this.parsedAfter);
    const rows: DiffRow[] = [];

    const type = this.field(payload, 'requestType') || 'Process';
    const month = this.field(payload, 'payMonth');
    const year = this.field(payload, 'payYear');
    const scope = this.field(payload, 'scope');
    const empCode = this.field(payload, 'empCode');
    const empName = this.selectedReq?.displayLabels?.['employeeName']?.trim();
    const settlement = this.field(payload, 'settlementFlag');
    const execType = this.field(payload, 'executionType');

    rows.push({ label: 'Operation', before: '', after: type, changed: false });
    const period = [month, year].filter(Boolean).join(' ');
    if (period) rows.push({ label: 'Period', before: '', after: period, changed: false });
    if (scope) rows.push({ label: 'Scope', before: '', after: this.payrollScopeLabel(scope), changed: false });

    if (scope?.toLowerCase() === 'single' && empCode) {
      rows.push({ label: 'Employee', before: '', after: empName ? `${empCode} — ${empName}` : empCode, changed: false });
    } else if (scope?.toLowerCase() === 'selective') {
      const filter = this.field(payload, 'entryWiseFilter');
      if (filter) rows.push({ label: 'Categories', before: '', after: filter, changed: false });
    }

    if (settlement && settlement.toUpperCase() === 'Y')
      rows.push({ label: 'Settlement', before: '', after: 'Yes', changed: false });
    if (execType) rows.push({ label: 'Execution', before: '', after: execType, changed: false });

    return rows;
  }

  private payrollScopeLabel(scope: string): string {
    switch ((scope || '').trim().toLowerCase()) {
      case 'single': return 'Single Employee';
      case 'selective': return 'Selective Employees';
      case 'all': return 'All Employees';
      default: return scope;
    }
  }

  /**
   * Company display for a Supplementary / LOP-Credit request's meta header:
   * resolved company name (enricher: displayLabels.companyName) with the code —
   * "C0001 — Acme"; falls back to the compCode when the name is absent.
   */
  get supplementaryCompanyDisplay(): string {
    const code = (this.selectedReq?.compCode ?? '').trim() || '—';
    const name = this.selectedReq?.displayLabels?.['companyName']?.trim();
    return name ? `${code} — ${name}` : code;
  }

  /**
   * Curated single-record rows for a Supplementary / LOP-Credit entry (a BULK,
   * multi-employee save): Entry Type (Supplementary / LOP Credit), Category
   * (name over code), Apply Period (Month Year) and the number of employees in
   * the batch (CREATE) or the targeted employee (DELETE). Read from the stored
   * envelope's request payload + the enricher's resolved labels. Rendered via the
   * CREATE/DELETE single-record table (no Before→After).
   */
  private supplementaryRows(): DiffRow[] {
    const payload = this.unwrapEnvelope(this.parsedAfter);
    const labels = this.selectedReq?.displayLabels ?? {};
    const rows: DiffRow[] = [];

    // Entry type (U supplementary, V LOP credit) — prefer the enricher label.
    const typeName = labels['componentTypeName']?.trim()
      || (this.selectedReq?.screenCode === 'LOP_CREDIT' ? 'LOP Credit' : 'Supplementary');
    rows.push({ label: 'Entry Type', before: '', after: typeName, changed: false });

    // Category — name over code (two-line) when the enricher resolved the name.
    const ccode = (this.field(payload, 'cCode') || labels['categoryCode'] || '').trim();
    const cname = labels['categoryName']?.trim() || '';
    if (ccode || cname) {
      rows.push({
        label: 'Category',
        before: '', after: cname || ccode,
        subBefore: '', subAfter: cname ? ccode : '',
        changed: false,
      });
    }

    // Apply period — Month Year.
    const month = this.field(payload, 'applyMonth') || labels['applyMonth'] || '';
    const year = this.field(payload, 'applyYear') || labels['applyYear'] || '';
    const period = [month, year].filter(Boolean).join(' ');
    if (period) rows.push({ label: 'Apply Period', before: '', after: period, changed: false });

    if (this.selectedReq?.transactionType === 'DELETE') {
      // DELETE: a single employee code, or the whole period when code is absent.
      const code = (this.field(payload, 'code') || labels['employeeCode'] || '').trim();
      rows.push({
        label: 'Scope',
        before: '', after: code ? `Employee ${code}` : 'All employees for the period',
        changed: false,
      });
    } else {
      // CREATE: the batch is multi-employee — show how many employees.
      const entries = payload['entries'] ?? payload['Entries'];
      const count = Array.isArray(entries) ? entries.length : Number(labels['employeeCount'] || 0);
      rows.push({ label: 'Employees', before: '', after: `${count} employee${count === 1 ? '' : 's'}`, changed: false });
    }

    return rows;
  }

  /**
   * Curated single-record rows for a manual Employee Increment entry: Employee
   * (code + resolved name), Apply Period, Effective Date, Before/After LOP, Days
   * Type, Entry Type and the Components as one readable "FieldName — Value" line
   * per component (increment value, falling back to the diff). Read from the stored
   * envelope's request payload. Rendered via the CREATE/DELETE single-record table
   * (no Before→After), with the Components cell wrapping instead of clipping.
   */
  private incrementRows(): DiffRow[] {
    const payload = this.unwrapEnvelope(this.parsedAfter);
    const labels = this.selectedReq?.displayLabels ?? {};
    const rows: DiffRow[] = [];

    const code = (this.field(payload, 'code') || '').trim();
    const name = labels['employeeName']?.trim();
    if (code || name) rows.push({ label: 'Employee', before: '', after: name ? `${code} — ${name}` : code, changed: false });

    const month = this.field(payload, 'applyMonth');
    const year = this.field(payload, 'applyYear');
    const period = [month, year].filter(Boolean).join(' ');
    if (period) rows.push({ label: 'Apply Period', before: '', after: period, changed: false });

    const effDate = this.formatDateOnly(this.field(payload, 'effDate'));
    if (effDate) rows.push({ label: 'Effective Date', before: '', after: effDate, changed: false });

    // DELETE carries no component values — Employee + period + effective date are enough.
    if (this.selectedReq?.transactionType === 'DELETE') return rows;

    const beforeLop = this.field(payload, 'beforeLop');
    const afterLop = this.field(payload, 'afterLop');
    if (beforeLop !== '') rows.push({ label: 'Before LOP', before: '', after: beforeLop, changed: false });
    if (afterLop !== '') rows.push({ label: 'After LOP', before: '', after: afterLop, changed: false });

    const daysType = this.field(payload, 'daysType');
    if (daysType) rows.push({ label: 'Days Type', before: '', after: this.incrementDaysTypeLabel(daysType), changed: false });

    const entryType = this.field(payload, 'entryType');
    if (entryType) rows.push({ label: 'Entry Type', before: '', after: this.incrementEntryTypeLabel(entryType), changed: false });

    // Components — one "FieldName — Value" line each (newlines; the cell renders
    // them stacked via white-space: pre-line). For a percentage entry the value is
    // the % increment; otherwise the new (incremented) value, with the difference
    // shown in parentheses when it differs.
    const components = this.incrementComponentLines(payload['components'] ?? payload['Components'], entryType);
    rows.push({ label: 'Components', before: '', after: components || '—', changed: false });

    return rows;
  }

  /** Maps the increment days-type code (L lop | P paid-days) to its label. */
  private incrementDaysTypeLabel(code: string): string {
    switch ((code || '').trim().toUpperCase()) {
      case 'L': return 'LOP Days';
      case 'P': return 'Paid Days';
      default: return code;
    }
  }

  /** Maps the increment entry-type code (D direct | P percentage) to its label. */
  private incrementEntryTypeLabel(code: string): string {
    switch ((code || '').trim().toUpperCase()) {
      case 'D': return 'Direct';
      case 'P': return 'Percentage';
      default: return code;
    }
  }

  /**
   * Formats the increment component values as "<FieldName> — <value>" lines, one
   * per component. Each item is an IncrementComponentValue { fieldName, curValue,
   * incValue, diffValue }. For a percentage entry the increment value is a %;
   * otherwise the new value is current + diff, shown with the "(+diff)" change.
   */
  private incrementComponentLines(value: unknown, entryType: string): string {
    if (!Array.isArray(value) || value.length === 0) return '';
    const isPercent = (entryType || '').trim().toUpperCase() === 'P';
    return value.map((item, i) => {
      const row = (item ?? {}) as Record<string, unknown>;
      const field = this.pick(row, 'fieldName') || `Component ${i + 1}`;
      const cur = this.pick(row, 'curValue');
      const inc = this.pick(row, 'incValue');
      const diff = this.pick(row, 'diffValue');
      if (isPercent) {
        return `${field} — ${inc}%`;
      }
      // Direct: new value = current + diff. Show the new value with the delta.
      const newVal = (cur !== '' || diff !== '')
        ? (Number(cur || 0) + Number(diff || 0))
        : inc;
      const delta = diff !== '' && Number(diff) !== 0
        ? ` (${Number(diff) > 0 ? '+' : ''}${diff})`
        : '';
      return `${field} — ${newVal}${delta}`;
    }).join('\n');
  }

  /**
   * Employee display for a Separation/Release request's meta header: resolved
   * name (enricher: displayLabels.employeeName) with the code — "E0001 — John
   * Smith"; falls back to the code (the transaction id) when the name is absent.
   */
  get separationEmployeeDisplay(): string {
    const code = (this.selectedReq?.transactionId ?? '').trim() || '—';
    const name = this.selectedReq?.displayLabels?.['employeeName']?.trim();
    return name ? `${code} — ${name}` : code;
  }

  /**
   * Curated single-record rows for a Separation / Release request. Separation
   * shows Separation Type, Last Working Date and Reason; Release shows Release
   * Date. (The employee is shown in the meta header, not repeated here.)
   */
  private separationRows(): DiffRow[] {
    const payload = this.unwrapEnvelope(this.parsedAfter);
    const rows: DiffRow[] = [];

    if (this.selectedReq?.screenCode === 'EMPLOYEE_RELEASE') {
      const releaseDate = this.formatDateOnly(this.field(payload, 'releaseDate'));
      if (releaseDate) rows.push({ label: 'Release Date', before: '', after: releaseDate, changed: false });
      return rows;
    }

    // EMPLOYEE_SEPARATION
    const type = this.separationTypeLabel(this.field(payload, 'termStatus'));
    const termDate = this.formatDateOnly(this.field(payload, 'termDate'));
    const reason = this.field(payload, 'reason');
    if (type) rows.push({ label: 'Separation Type', before: '', after: type, changed: false });
    if (termDate) rows.push({ label: 'Last Working Date', before: '', after: termDate, changed: false });
    if (reason) rows.push({ label: 'Reason', before: '', after: reason, changed: false });
    return rows;
  }

  /** Maps the separation term-status code (T/S/R/O) to its label. */
  private separationTypeLabel(code: string): string {
    switch ((code || '').trim().toUpperCase()) {
      case 'T': return 'Separation';
      case 'S': return 'Suspension';
      case 'R': return 'Resignation';
      case 'O': return 'Other Reason';
      default: return code;
    }
  }

  /**
   * Curated single-record rows for a Loan Deviation: Loan (description over
   * code), Loan Date, Loan Amount (when the enricher resolved it) and the
   * Installments schedule (one line per installment: Month, Year, Amount/Month).
   * Rendered via the CREATE/DELETE single-record table (no Before→After).
   */
  private loanDeviationRows(): DiffRow[] {
    const payload = this.unwrapEnvelope(this.parsedAfter);

    const loanCode = this.field(payload, 'loanCode');
    const loanDesc = this.selectedReq?.displayLabels?.['loanDescription']?.trim() || '';
    const loanDate = this.formatDateOnly(this.field(payload, 'loanDate'));
    // Loan Amount and No. of Months are NOT in the deviation payload; shown only
    // when the backend enricher resolves them (displayLabels.loanAmount /
    // noOfMonths). Omitted otherwise.
    const loanAmount = this.selectedReq?.displayLabels?.['loanAmount']?.trim() || '';
    const noOfMonths = this.selectedReq?.displayLabels?.['noOfMonths']?.trim() || '';

    const rows: DiffRow[] = [];

    // Loan — two-line (description over code) in the single-record cell.
    rows.push({
      label: 'Loan',
      before: '', after: loanDesc || loanCode,
      subBefore: '', subAfter: loanDesc ? loanCode : '',
      changed: false,
    });

    if (loanDate) rows.push({ label: 'Loan Date', before: '', after: loanDate, changed: false });
    if (loanAmount) rows.push({ label: 'Loan Amount', before: '', after: loanAmount, changed: false });
    // No. of Months — placed right after Loan Amount (as its own column in the
    // single-record table).
    if (noOfMonths) rows.push({ label: 'No. of Months', before: '', after: noOfMonths, changed: false });

    // Installments — the deviated schedule, one readable line per installment.
    const installments = this.formatInstallments(payload['rows'] ?? payload['Rows']);
    rows.push({ label: 'Installments', before: '', after: installments || '—', changed: false });

    return rows;
  }

  /**
   * Formats the deviation installment rows as "<No>  <Month> <Year> — <Amount>"
   * lines. When the loan type carries interest (enricher:
   * displayLabels.loanHasInterest = 'Y'), each line also shows "(int <amount>)".
   */
  private formatInstallments(value: unknown): string {
    if (!Array.isArray(value) || value.length === 0) return '';
    const showInterest = (this.selectedReq?.displayLabels?.['loanHasInterest'] ?? '')
      .toString().trim().toUpperCase() === 'Y';
    return value.map((item, i) => {
      const row = (item ?? {}) as Record<string, unknown>;
      const inst = this.pick(row, 'installmentNo') || String(i + 1);
      const month = this.monthShort(this.pick(row, 'payMonthNo'));
      const year = this.pick(row, 'payYearNo');
      const amt = this.pick(row, 'amtPerMonth');
      const interest = this.pick(row, 'interestAmt');
      const period = [month, year].filter(Boolean).join(' ');
      const left = period ? `${inst}  ${period}` : `${inst}`;
      let line = amt !== '' ? `${left} — ${amt}` : left;
      if (showInterest && interest !== '') line += ` (int ${interest})`;
      return line;
    }).join('\n');
  }

  /** Employee code parsed from a Loan request's transaction id (first segment). */
  get loanEmployeeCode(): string {
    const parts = (this.selectedReq?.transactionId ?? '').split('|');
    return parts[0]?.trim() || '—';
  }

  /**
   * Employee display for a Loan request's meta header. Prefers the resolved
   * employee name (backend enricher: displayLabels.employeeName) and shows it
   * with the code, e.g. "E0001 — John Smith"; falls back to just the code when
   * the name was not resolved.
   */
  get loanEmployeeDisplay(): string {
    const code = this.loanEmployeeCode;
    const name = this.selectedReq?.displayLabels?.['employeeName']?.trim();
    return name ? `${code} — ${name}` : code;
  }

  transactionLabel(tt: string): string {
    return tt === 'CREATE' ? `New ${this.entity}`
         : tt === 'UPDATE' ? `Update ${this.entity}`
         : tt === 'DELETE' ? `Delete ${this.entity}`
         : tt === 'BULK_IMPORT' ? 'Bulk Import'
         : tt;
  }

  statusSeverity(status: string): string {
    switch (status) {
      case 'APPROVED':  return 'success';
      case 'REJECTED':  return 'danger';
      case 'RETURNED':  return 'warning';
      case 'CANCELLED': return 'secondary';
      default:          return 'info';   // PENDING / SUBMITTED
    }
  }

  detailsSectionTitle(): string {
    // Loan Deviation shows a curated single-record view (not a diff).
    if (this.isLoanDeviation) return 'Loan Deviation Details';
    if (this.selectedReq?.screenCode === 'EMPLOYEE_SEPARATION') return 'Separation Details';
    if (this.selectedReq?.screenCode === 'EMPLOYEE_RELEASE') return 'Release Details';
    if (this.isPayrollScreen) return 'Payroll Run Details';
    if (this.selectedReq?.screenCode === 'SUPPLEMENTARY') return 'Supplementary Details';
    if (this.selectedReq?.screenCode === 'LOP_CREDIT') return 'LOP Credit Details';
    if (this.isIncrementScreen) return this.selectedReq?.transactionType === 'DELETE'
      ? 'Increment to be Deleted' : 'New Increment Entry Details';
    const tt = this.selectedReq?.transactionType;
    if (this.isMonthlyInput) {
      return tt === 'DELETE' ? 'Monthly Input Records to be Deleted' : 'Monthly Input to be Saved';
    }
    return tt === 'CREATE' ? `New ${this.entity} Details`
         : tt === 'UPDATE' ? 'Changes (Before → After)'
         : tt === 'DELETE' ? `${this.entity} to be Deleted`
         : 'Details';
  }

  /** Date + time as dd/MM/yyyy hh:mm AM/PM in the user's local time zone. */
  formatDateTime(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(this.toUtcIso(iso));
    return isNaN(d.getTime()) ? iso : formatAngularDate(d, 'dd/MM/yyyy hh:mm a', 'en-US');
  }

  /** Date-only value (e.g. Date of Incorporation) as dd/MM/yyyy, formatted in UTC. */
  private formatDateOnly(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : formatAngularDate(d, 'dd/MM/yyyy', 'en-US', 'UTC');
  }

  /**
   * The API stores these timestamps in UTC (GETUTCDATE) but serialises them without a zone
   * designator. Mark them as UTC so they convert to the user's local time correctly.
   */
  private toUtcIso(iso: string): string {
    return /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
  }

  // ── Diff rows (mirrors the Company Approvals screen) ─────────────────────────

  get diffRows(): DiffRow[] {
    if (!this.selectedReq) return [];

    // Loan Deviation gets a curated single-record view (Loan / Loan Date / Loan
    // Amount / Installments) instead of a Before→After diff — the operation only
    // changes the installment schedule, so a diff is not meaningful.
    if (this.isLoanDeviation) return this.loanDeviationRows();

    // Separation / Release get a curated single-record view (type / dates /
    // reason) instead of a Before→After diff.
    if (this.isSeparationScreen) return this.separationRows();

    // Payroll Processing gets a curated single-record view (operation / period /
    // scope / target) instead of a Before→After diff.
    if (this.isPayrollScreen) return this.payrollRows();

    // Supplementary / LOP Credit get a curated single-record view (type /
    // category / period / employee count) instead of a Before→After diff.
    if (this.isSupplementaryScreen) return this.supplementaryRows();

    // Employee Increment (manual entry/delete) gets a curated single-record view
    // (employee / period / effective date / LOP / components) so the Components
    // read as "FieldName — Value" lines instead of a clipped object dump.
    if (this.isIncrementScreen) return this.incrementRows();

    // Screens WITHOUT a curated field map (e.g. the four LOAN_* screens, and any
    // future screen) render generically from the payload's own keys — the same
    // approach as the cross-screen approvals inbox (ApprovalListComponent). This
    // is why the Loan request now shows its "New Loan Entry Details" table here,
    // matching the Dashboard approvals page. The curated screens (COMPANY /
    // CATEGORY / EMPLOYEE) keep their hand-tuned labels/field order below.
    if (!this.hasCuratedFieldMap()) return this.genericDiffRows();

    const tt = this.selectedReq.transactionType;
    const before = this.parsedBefore;
    const afterRaw = this.parsedAfter;
    const after = this.unwrapEnvelope(afterRaw);

    // Read a value trying the given key plus any known aliases. DELETE requests
    // serialize the DB ENTITY (e.g. Category -> CName/CpOrder), while CREATE/UPDATE
    // serialize the request DTO (categoryName/displayOrder); the aliases bridge both.
    const read = (obj: Record<string, unknown> | null, key: string): string => {
      const direct = this.field(obj, key);
      if (direct) return direct;
      for (const alt of this.fieldAliases(key)) {
        const v = this.field(obj, alt);
        if (v) return v;
      }
      return '';
    };

    const bv = (f: string): string => read(before, f);
    const av = (fb: string, fa: string): string => {
      if (tt === 'DELETE') return '—';
      return read(after, fa) || read(afterRaw, fa) || read(afterRaw, fb) || read(after, fb);
    };

    const { fields, dateFields } = this.fieldMap();

    return fields
      .map(([label, bField, aField]) => {
        let beforeVal = bv(bField);
        let afterVal = av(bField, aField);
        if (dateFields.has(bField)) {
          beforeVal = this.formatDateOnly(beforeVal);
          afterVal = this.formatDateOnly(afterVal);
        }
        return { label, before: beforeVal, after: afterVal, changed: beforeVal !== afterVal && !!beforeVal && !!afterVal };
      })
      .filter(r => r.before || r.after);
  }

  /** Screens that have a curated (hand-tuned) field map in fieldMap(). */
  private hasCuratedFieldMap(): boolean {
    const sc = this.selectedReq?.screenCode;
    return sc === 'COMPANY' || sc === 'CATEGORY' || sc === 'EMPLOYEE';
  }

  /**
   * Generic field table for screens without a curated field map (Loan, etc.).
   * Builds rows from the union of the payload keys, unwrapping the UPDATE/CREATE
   * envelope ({ ..., request:{...} }) and skipping internal/envelope keys. Keys
   * that look like dates are rendered dd/MM/yyyy. Mirrors
   * ApprovalListComponent.diffRows so this dialog matches the Dashboard page.
   */
  private genericDiffRows(): DiffRow[] {
    const tt = this.selectedReq!.transactionType;
    const before = this.unwrapEnvelope(this.parsedBefore);
    const after = this.unwrapEnvelope(this.parsedAfter);

    // On Loan screens the standalone "loanCode" column is replaced by a single
    // two-line "Loan" column (loan description over loan code). The description
    // key is used when present in the payload; otherwise only the code is shown
    // (the saved payload carries the code, not the description).
    const loan = this.isLoanScreen;
    const descKeys = new Set(['loandesc', 'loandescription', 'loandescname']);
    const skip = new Set(['request', 'compcode', 'code', 'id']);
    // On Loan screens the description key is folded into the Loan column, so hide
    // it as its own row.
    if (loan) for (const d of descKeys) skip.add(d);

    const keys: string[] = [];
    const pushKeys = (obj: Record<string, unknown>) => {
      for (const k of Object.keys(obj)) {
        if (skip.has(k.toLowerCase())) continue;
        if (!keys.some(e => e.toLowerCase() === k.toLowerCase())) keys.push(k);
      }
    };
    if (tt !== 'CREATE') pushKeys(before);
    if (tt !== 'DELETE') pushKeys(after);

    // Resolve the loan description for the first line of the two-line Loan
    // column. Priority: the backend enricher's resolved label
    // (displayLabels.loanDescription) — looked up from the loan code — then any
    // description key that happens to be in the payload. When neither exists only
    // the loan code shows.
    const resolvedDesc = this.selectedReq!.displayLabels?.['loanDescription']?.trim() || '';
    const descOf = (obj: Record<string, unknown>): string => {
      if (resolvedDesc) return resolvedDesc;
      for (const d of descKeys) { const v = this.field(obj, d); if (v) return v; }
      return '';
    };
    const beforeDesc = descOf(before);
    const afterDesc = descOf(after);

    return keys
      .map(key => {
        let b = this.field(before, key);
        let a = this.field(after, key);
        if (this.looksLikeDateKey(key)) {
          b = this.formatDateOnly(b) || b;
          a = this.formatDateOnly(a) || a;
        }
        // Loan screens: relabel "loanCode" to "Loan" and render it as a two-line
        // cell — description on top (when available), loan code underneath.
        if (loan && key.toLowerCase() === 'loancode') {
          return {
            label: 'Loan',
            before: beforeDesc || b, after: afterDesc || a,
            subBefore: beforeDesc ? b : '', subAfter: afterDesc ? a : '',
            changed: tt === 'UPDATE' && (beforeDesc || b) !== (afterDesc || a) && !!(beforeDesc || b) && !!(afterDesc || a),
          };
        }
        return { label: this.humanize(key), before: b, after: a, changed: tt === 'UPDATE' && b !== a && !!b && !!a };
      })
      .filter(r => r.before || r.after);
  }

  /** Heuristic: payload keys that hold a date (so the generic renderer formats them). */
  private looksLikeDateKey(key: string): boolean {
    return /date$/i.test(key) || key.toLowerCase() === 'doj' || key.toLowerCase() === 'dob' || key.toLowerCase() === 'doi';
  }

  /** Convert a payload key (camelCase / PascalCase / snake) into a readable label. */
  private humanize(key: string): string {
    const spaced = key
      .replace(/[_\-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  /**
   * Alternative payload keys for a field. DELETE requests serialize the DB entity
   * (PascalCase, e.g. Category.CName / Category.CpOrder), while CREATE/UPDATE
   * serialize the request DTO (categoryName / displayOrder). Returning the entity
   * equivalents here lets the diff resolve a value regardless of which shape the
   * request stored. The lookup itself is case-insensitive, so only genuinely
   * different names need listing.
   */
  private fieldAliases(key: string): string[] {
    switch (key) {
      case 'categoryName': return ['CName'];
      case 'displayOrder': return ['CpOrder'];
      default:             return [];
    }
  }

  /**
   * Field map for the diff/record table, keyed by the request's screen. Each entry is
   * [label, beforeKey, afterKey]; dateFields are rendered as dd/MM/yyyy.
   */
  private fieldMap(): { fields: [string, string, string][]; dateFields: Set<string> } {
    if (this.selectedReq?.screenCode === 'CATEGORY') {
      return {
        fields: [
          ['Category Name', 'categoryName', 'categoryName'],
          ['Display Order', 'displayOrder', 'displayOrder'],
        ],
        dateFields: new Set<string>(),
      };
    }

    if (this.selectedReq?.screenCode === 'EMPLOYEE') {
      return {
        fields: [
          ['Employee Code', 'code',        'code'],
          ['Employee Name', 'empName',     'empName'],
          ['Category',      'ccode',       'ccode'],
          ['Designation',   'designation', 'designation'],
          ['Date of Join',  'doj',         'doj'],
        ],
        dateFields: new Set(['doj']),
      };
    }

    // Default: COMPANY.
    return {
      fields: [
        ['Company Name',   'compName',  'companyName'],
        ['Short Name',     'shortName', 'shortName'],
        ['Address 1',      'compAdd1',  'address1'],
        ['Address 2',      'compAdd2',  'address2'],
        ['City',           'compAdd3',  'address3'],
        ['Pin Code',       'compAdd4',  'address4'],
        ['Phone',          'phone',     'phone'],
        ['Fax',            'fax',       'fax'],
        ['Date of Incorp', 'doi',       'doi'],
      ],
      dateFields: new Set(['doi']),
    };
  }

  private field(obj: CompanyPayload | Record<string, unknown> | null, key: string): string {
    if (!obj) return '';
    const record = obj as Record<string, unknown>;
    let value: unknown;
    if (key in record && record[key] != null) {
      value = record[key];
    } else {
      const lower = key.toLowerCase();
      const match = Object.keys(record).find(k => k.toLowerCase() === lower);
      value = match ? record[match] : undefined;
    }
    return value != null ? this.formatValue(key, value) : '';
  }

  /**
   * Renders a payload value as display text. Scalars become their string form;
   * ARRAYS (e.g. a loan deviation's "rows" installment schedule) are formatted
   * as one readable line per element instead of the default
   * "[object Object],[object Object]" that String(array) produces. The lines are
   * newline-joined; the detail table renders them with white-space: pre-line.
   */
  private formatValue(key: string, value: unknown): string {
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      return value
        .map((item, i) => this.formatArrayItem(key, item, i))
        .join('\n');
    }
    if (value !== null && typeof value === 'object') {
      // A single nested object: show its key:value pairs on one line.
      return Object.entries(value as Record<string, unknown>)
        .map(([k, v]) => `${this.humanize(k)}: ${v ?? '—'}`)
        .join(', ');
    }
    return String(value);
  }

  /** Formats one element of an array value — a loan installment row when it looks
   *  like one, otherwise a generic "key: value" summary. */
  private formatArrayItem(parentKey: string, item: unknown, index: number): string {
    if (item === null || typeof item !== 'object') return String(item);
    const row = item as Record<string, unknown>;
    // Loan deviation installment row (LoanScheduleRowDto): installmentNo /
    // payMonthNo / payYearNo / amtPerMonth / interestAmt.
    const inst = this.pick(row, 'installmentNo');
    const month = this.pick(row, 'payMonthNo');
    const year = this.pick(row, 'payYearNo');
    const amt = this.pick(row, 'amtPerMonth');
    const interest = this.pick(row, 'interestAmt');
    if (inst !== '' || amt !== '') {
      const label = inst !== '' ? `#${inst}` : `#${index + 1}`;
      const period = (month !== '' || year !== '')
        ? ` ${this.monthShort(month)}${year !== '' ? ' ' + year : ''}`
        : '';
      const amount = amt !== '' ? ` — ${amt}` : '';
      const intr = interest !== '' && Number(interest) !== 0 ? ` (int ${interest})` : '';
      return `${label}${period}${amount}${intr}`.trim();
    }
    // Fallback: generic "k: v" summary for an unrecognised object shape.
    return Object.entries(row).map(([k, v]) => `${this.humanize(k)}: ${v ?? '—'}`).join(', ');
  }

  /** Case-insensitive single-key read returning '' when absent/null. */
  private pick(obj: Record<string, unknown>, key: string): string {
    if (key in obj && obj[key] != null) return String(obj[key]);
    const lower = key.toLowerCase();
    const match = Object.keys(obj).find(k => k.toLowerCase() === lower);
    const v = match ? obj[match] : undefined;
    return v != null ? String(v) : '';
  }

  /** 1-12 -> short month name; passes through anything else unchanged. */
  private monthShort(m: string): string {
    const n = Number(m);
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return (n >= 1 && n <= 12) ? names[n - 1] : m;
  }

  private unwrapEnvelope(payload: CompanyPayload | Record<string, unknown> | null): Record<string, unknown> {
    if (!payload) return {};
    const record = payload as Record<string, unknown>;
    const requestKey = Object.keys(record).find(k => k.toLowerCase() === 'request');
    const inner = requestKey ? record[requestKey] : undefined;
    return (inner && typeof inner === 'object') ? inner as Record<string, unknown> : record;
  }

  private tryParse(json?: string | null): CompanyPayload | null {
    if (!json) return null;
    try { return JSON.parse(json) as CompanyPayload; } catch { return null; }
  }
}
